import Grid from '@mui/material/Grid';
import * as Yup from 'yup';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { Contact } from '../../models/contact';
import { Button, Dialog } from '@mui/material';
import { useSaveData } from '../../hooks/useSaveData';
import { contactApiKeyStore } from '../../api/contact-api-key-store';
import { ContactForm } from './contact.form';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';

interface CustomerForm {
  contact?: any;
  onSaveContact: (contact: Contact) => void;
  isSyncWithDux?: boolean;
  page?: string;
  pageType?: string;
  itemId?: string;
}

const contactValidationSchema = Yup.object().shape({
  contactsFirstName: Yup.string().required('First name is required'),
  contactsLastName: Yup.string().required('Last name is required'),
  contactsWorkEmail: Yup.string().email('Must be a valid email').required('Email is required'),
  contactsSecondaryEmail: Yup.string().email('Must be a valid email'),
  contactsOfficePhone: Yup.string().matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number ' }),
  contactsCellPhone: Yup.string().matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' }),
  contactsOtherPhone: Yup.string().matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' }),

  contactRole: Yup.object()
    .nullable()
    .when(
      ['ppiReportStatusIDs', 'susReportStatusIDs', 'otherReportStatusIDs'],
      {
        is: (ppi: any, sus: any, other: any) =>
          (ppi?.length ?? 0) > 0 ||
          (sus?.length ?? 0) > 0 ||
          (other?.length ?? 0) > 0,

        then: (schema) => schema.required('Contact Role is required.'),

        otherwise: (schema) => schema.nullable().notRequired(),
      }
    )
});

const contactWithPhoneValidationSchema = Yup.object().shape({
  contactsFirstName: Yup.string().required('First name is required'),
  contactsLastName: Yup.string().required('Last name is required'),
  contactsWorkEmail: Yup.string().email('Must be a valid email').required('Email is required'),
  contactsSecondaryEmail: Yup.string().email('Must be a valid email'),
  contactsOfficePhone: Yup.string().matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number ' }),
  contactsCellPhone: Yup.string().required('Cell phone is required').matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' }),
  contactsOtherPhone: Yup.string().matches(/^[0-9]+$/, { excludeEmptyString: true, message: 'Invalid phone number' }),

  contactRole: Yup.object()
    .nullable()
    .when(
      ['ppiReportStatusIDs', 'susReportStatusIDs', 'otherReportStatusIDs'],
      {
        is: (ppi: any, sus: any, other: any) =>
          (ppi?.length ?? 0) > 0 ||
          (sus?.length ?? 0) > 0 ||
          (other?.length ?? 0) > 0,

        then: (schema) => schema.required('Contact Role is required.'),

        otherwise: (schema) => schema.nullable().notRequired(),
      }
    )


});

export const SaveContactComponent = ({ contact, onSaveContact, isSyncWithDux, page, pageType, itemId }: CustomerForm) => {
  const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';
  const [isDuxSelected, setDuxSelected] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [contactDetails, setContactDetails] = useState<any>()

  const form = useReactHookForm({
    defaultValues: {
      ...(contact && contact),
      isActive: true,
      // isMasterContacts: page === 'workOrderList' || page === 'unit',
      contactRole: contact?.contactsRoleID ? { contactsRoleID: contact?.contactsRoleID, contactsRoleName: '' } : null,
    },
    yupSchema: isDuxSelected ? contactWithPhoneValidationSchema : contactValidationSchema,
  });

  useEffect(() => {
    if (contact) {
      if (page !== 'municipality' && page !== 'unit' && page !== 'workOrderList' && isDUXEnabled) {
        setDuxSelected(contact?.syncWithDux ?? false)
      }
    }
  }, [contact])

  const { isLoading, mutateAsync } = useSaveData<Contact>(
    contact?.contactsID ? contactApiKeyStore.updateContact() : contactApiKeyStore.createContact()
  );
  const { id, officeID, projectID, unitID } = useParams();

  const saveContact = async (value: any) => {
    const extendedValue = {
      ...value, // Retain all original properties
      contactsRoleID: value?.contactRole?.contactsRoleID,
      customerID: !id ? 0 : Number(id),
      officeID: !officeID ? 0 : Number(officeID),
      projectID: !projectID ? 0 : Number(projectID),
      unitID: !unitID ? (!itemId ? 0 : Number(itemId)) : Number(unitID),
      isMunicipalContact: page === 'municipality',
      contactMappingFrom: page,
      contactsNotificationIDs: value?.contactsNotificationIDs?.length > 0 ? value?.contactsNotificationIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
      ppiReportStatusIDs: value?.ppiReportStatusIDs?.length > 0 ? value?.ppiReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
      susReportStatusIDs: value?.susReportStatusIDs?.length > 0 ? value?.susReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
      otherReportStatusIDs: value?.otherReportStatusIDs?.length > 0 ? value?.otherReportStatusIDs?.map((ps: any) => ps.notificationReportStatusID) : [],
    };

    const result = await mutateAsync({ data: extendedValue });
    onSaveContact(result?.data ?? []);
  }

  const handleSubmit = async (value: any) => {
    if ((page == 'office' || page == 'project') && pageType == 'edit') {
      if (value?.isMasterContacts) {
        setOpenDialog(true);
        setContactDetails(value);
      }
      else {
        saveContact(value);
      }
    }
    else { saveContact(value); }
  };

  const handleConfirmMasterContact = () => {
    saveContact({ ...contactDetails, copyMasterContactConfirmation: true });
    setOpenDialog(false);
  }

  const handleNoteConfirmMasterContact = () => {
    saveContact({ ...contactDetails, copyMasterContactConfirmation: false });
    setOpenDialog(false);
  }

  const handleSubmitClick = () => {
    form.handleSubmit(handleSubmit)();
  };

  const handleDuxAction = (value: any) => { setDuxSelected(value); }

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <ReactHookForm formName="SAVE_CONTACT_FORM" onSubmit={handleSubmit} formContextProps={form} isBusy={isLoading}>
        <Grid container>
          <ContactForm page={page} isSyncWithDux={isSyncWithDux} handleDuxAction={handleDuxAction} />
          <Grid container justifyContent="flex-end" spacing={1}>
            <Grid item>
              <Button
                type="button"
                variant="contained"
                disableElevation={true}
                disabled={isLoading || isReadOnlyPermission}
                onClick={handleSubmitClick}
              >
                Save
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </ReactHookForm>
      {openDialog && (
        <Dialog
          open={openDialog}
          className="modalpopup"
          title={'CONFIRM STATUS'}
        // onClose={closeDialog}
        >
          <div>
            <div style={{ padding: '25px 50px', textAlign: 'center' }}>
              <div>
                {
                  page == 'office' ?
                    `Are you sure you want to copy this master contact to all projects under this office?`
                    :
                    'Are you sure you want to copy this master contact to all lots under this project?'
                }
              </div>
              {/* <LoadingIndicator isLoading={isLoadingUnitComplete} /> */}
            </div>
          </div>
          <DialogActionsBar>
            {isReadOnlyPermission && (
              <Button style={{ maxWidth: '140px' }} size='small' type="button" variant="outlined" onClick={() => setOpenDialog(false)}>
                Cancel
              </Button>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: 2 }} className="  ">
              <button
                className="btn btn-sm btn-primary btn-lightGrey"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2, marginRight: 6
                }}
                disabled={isReadOnlyPermission}
                onClick={handleNoteConfirmMasterContact}
                autoFocus
              >
                No
              </button>

              <button
                className="btn btn-sm btn-primary  btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#0000b5',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer', padding: 2
                }}
                type="button"
                onClick={handleConfirmMasterContact}
                disabled={isLoading || isReadOnlyPermission}
              >
                Yes
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};
